<script lang="ts">
import { defineComponent, PropType, ref, watch, computed } from 'vue';
import type { Group } from '@/types/Groups';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default defineComponent({
  name: 'TreeSelectView',
  components: { Select, SelectTrigger, SelectValue, SelectContent, SelectItem },
  props: {
    groupTree: { type: Array as PropType<Group[]>, required: true },
    modelValue: { type: String, required: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    // Local ref for two-way binding
    const selected = ref(props.modelValue ?? '');

    // Keep local ref in sync if prop changes
    watch(() => props.modelValue, (newVal) => {
      selected.value = newVal ?? '';
    });

    const flattenedGroups = computed(() => {
      const result: { id: number; name: string; level: number }[] = [];
      const traverse = (groups: Group[], level = 0) => {
        for (const group of groups) {
          result.push({ id: group.id, name: group.name, level });
          if (group.children?.length) traverse(group.children, level + 1);
        }
      };
      traverse(props.groupTree);
      return result;
    });

    const handleSelect = (id: string) => {
      selected.value = id;       // update local ref
      emit('update:modelValue', id); // emit change
    };

    const selectedGroupName = computed(() => {
      const sel = flattenedGroups.value.find(g => g.id.toString() === selected.value);
      return sel?.name ?? '';
    });

    return { selected, flattenedGroups, handleSelect, selectedGroupName };
  },
});
</script>

<template>
<Select v-model="selected" @value-change="handleSelect">
  <SelectTrigger>
    <SelectValue>{{ selectedGroupName || 'Select Group' }}</SelectValue>
  </SelectTrigger>
  <SelectContent>
    <SelectItem
      v-for="group in flattenedGroups"
      :key="group.id"
      :value="group.id.toString()"
    >
      <span :style="{ paddingLeft: group.level * 16 + 'px' }">{{ group.name }}</span>
    </SelectItem>
  </SelectContent>
</Select>

</template>

